import { useSelector } from 'react-redux'
import { isAuthTokenSupported } from '../../../auth'
import { useIntl, defineMessages } from 'react-intl'
import { useAppSelector } from '../../../store/hooks'
import { selectHomeData } from '../../../store/selectors/home'
import { SEARCH_PATH_HELPERS } from '../../../constants/navigation'
import { selectClientFetchId } from '../../../store/selectors/client'
import { ListingCompact } from '../../../api/gqlaxy/gql/generated/graphql'
import { ListingCardNew } from '../../homeSearch/ListingCardNew/ListingCardNew'
import { useGetSingleFeedForZipCode } from '../../../hooks/gallery/useGetSingleFeedForZipCode'
import { Box, Card, Flex, Text, Heading, CardBody, LinkButton } from '@homebotapp/hb-react-component-catalog'
import { HOMEOWNER_DIGEST_GALLERY_MODULE } from '../../../constants/tracking/trackingEventLocations/homeownerDigestTrackingEventLocation'

export const MSG = defineMessages({
  head: {
    id: 'homeowners.galleryModule.head',
    defaultMessage: 'Gallery'
  },
  recentlySoldSubHead: {
    id: 'homeowners.galleryModule.recentlySoldSubHead',
    defaultMessage: 'Recently Sold in {zip}'
  },
  body: {
    id: 'homeowners.galleryModule.body',
    defaultMessage: 'Explore to see what the market is like'
  },
  viewHomesBtn: {
    id: 'homeowners.galleryModule.viewHomesBtn',
    defaultMessage: 'View all homes'
  }
})

export interface GalleryModuleProps {}

export const GalleryModule = ({}: GalleryModuleProps) => {
  const intl = useIntl()
  const home = useSelector(selectHomeData)
  const clientId = useSelector(selectClientFetchId)
  const customerProfileId = useAppSelector(state => state.customerProfile.data?.id)
  const { data, isFetching } = useGetSingleFeedForZipCode(
    { input: { zipCode: home?.address?.zip } },
    { enabled: isAuthTokenSupported() && !!home?.address?.zip }
  )
  const listings = data?.ephemeralFeeds?.[0]?.listings

  if (!listings?.length || listings.length === 0) return null

  return (
    <Box mt={['-8.5rem', '-6rem', null, '-4.5rem']}>
      {listings && (
        <Flex
          w={['130%', '115%', null, '130%']}
          zIndex={1}
          direction='row'
          position='relative'
          transform={[
            'translateY(95px)translateX(-11.5%)',
            'translateY(75px)translateX(-6.5%)',
            null,
            'translateY(65px)translateX(-12%)'
          ]}
        >
          {listings[1] && (
            <Box
              w='300px'
              bottom='0'
              left='0'
              borderRadius='2xl'
              boxShadow='var(--hb-shadows-lg)'
              position='absolute'
              transition='all .25s ease-out'
              transform={['rotate(-5deg)scale(.6)', 'rotate(-5deg)scale(.7)', 'rotate(-5deg)scale(.75)']}
              _hover={{ transform: 'scale(.9)translateY(-25px)rotate(-5deg)', zIndex: 2 }}
            >
              <ListingCardNew
                listing={listings[1] as ListingCompact}
                tagPreference='soldDate'
                tracking={{
                  guid: '8wfvzQ1F7hF4RUaN3Rko5N',
                  ui_context: HOMEOWNER_DIGEST_GALLERY_MODULE,
                  descriptor: 'Gallery On Own Listing Clicked'
                }}
              />
            </Box>
          )}
          {listings?.[0] && (
            <Box
              w='300px'
              bottom='0'
              mx='auto'
              position='relative'
              zIndex={1}
              borderRadius='2xl'
              boxShadow='var(--hb-shadows-xl)'
              transform={['scale(.65)translateY(-20px)', 'scale(.75)translateY(-20px)', 'scale(.85)translateY(-25px)']}
              transition='all .25s ease-out'
              _hover={{ transform: 'scale(.95)translateY(-50px)' }}
            >
              <ListingCardNew
                listing={listings[0] as ListingCompact}
                tagPreference='soldDate'
                tracking={{
                  guid: 'mG5wo1Vw49EixDwb3DNmoM',
                  ui_context: HOMEOWNER_DIGEST_GALLERY_MODULE,
                  descriptor: 'Gallery On Own Listing Clicked'
                }}
              />
            </Box>
          )}
          {listings?.[2] && (
            <Box
              w='300px'
              bottom='0'
              right='0'
              borderRadius='2xl'
              position='absolute'
              boxShadow='var(--hb-shadows-lg)'
              transition='all .25s ease-out'
              transform={['rotate(5deg)scale(.6)', 'rotate(5deg)scale(.7)', 'rotate(5deg)scale(.75)']}
              translateY='25px'
              _hover={{ transform: 'scale(.9)translateY(-25px)rotate(5deg)', zIndex: 2 }}
            >
              <ListingCardNew
                listing={listings[2] as ListingCompact}
                tagPreference='soldDate'
                tracking={{
                  guid: 'xzSwSKEjT78jymiFP61ebH',
                  ui_context: HOMEOWNER_DIGEST_GALLERY_MODULE,
                  descriptor: 'Gallery On Own Listing Clicked'
                }}
              />
            </Box>
          )}
        </Flex>
      )}
      <Card size='lg'>
        <CardBody display='flex' flexDirection={['column', null, 'row']}>
          <Box>
            <Heading mt={4} mb={0} size='2xs' variant='eyebrow' color='brand' textAlign='left'>
              {intl.formatMessage(MSG.head)}
            </Heading>
            <Heading mb={2} as='h3' size='md' textAlign='left'>
              {intl.formatMessage(MSG.recentlySoldSubHead, { zip: home?.address?.zip })}
            </Heading>
            <Text>{intl.formatMessage(MSG.body)}</Text>
          </Box>
          <LinkButton
            my='auto'
            size='sm'
            colorScheme='primary'
            ml={[0, null, 'auto']}
            isDisabled={isFetching}
            href={SEARCH_PATH_HELPERS.forYouTab.buildPath(customerProfileId, clientId)}
            tracking={{
              guid: 'fZ7G8An6ZwdyT5Svj317zE',
              ui_context: HOMEOWNER_DIGEST_GALLERY_MODULE,
              descriptor: 'View all homes'
            }}
          >
            {intl.formatMessage(MSG.viewHomesBtn)}
          </LinkButton>
        </CardBody>
      </Card>
    </Box>
  )
}
